import { useState } from "react";
import { ChevronDown, ChevronRight, Gauge, Database } from "lucide-react";

const SpeedFuelCalculations = () => {
  const [openSections, setOpenSections] = useState(false);

  const toggleSection = () => {
    setOpenSections((prev) => !prev);
  };

  const calculations = [
    {
      id: "engine-filtering",
      title: "Engine ID Filtering",
      description: "Regex matching for pipe-separated engine IDs",
      formula:
        "engineId: { $regex: new RegExp(`(^|\\|)${engineId}(\\||$)`), $options: 'i' }",
      borderColor: "border-green-200",
      bgColor: "bg-green-50",
      textColor: "text-green-700",
      code: `engineId: { 
  $regex: new RegExp(\`(^|\\|)\${engineId}(\\||$)\`), 
  $options: "i" 
}`,
      fields: ["engineId"],
    },
    {
      id: "fuel-consumption-calculation",
      title: "Fuel Consumption Source",
      description: "How fuelConsumption is calculated from CSV data",
      formula:
        "Main_Engine_Cons_MT_HFO + Main_Engine_Cons_MT_LFO + Main_Engine_Cons_MT_MGO",
      borderColor: "border-red-200",
      bgColor: "bg-red-50",
      textColor: "text-red-700",
      code: `const mainEngineFuelConsumption =
  (parseFloat(row["Main_Engine_Cons_MT_HFO"]) || 0) +
  (parseFloat(row["Main_Engine_Cons_MT_LFO"]) || 0) +
  (parseFloat(row["Main_Engine_Cons_MT_MGO"]) || 0);`,
      fields: [
        "Main_Engine_Cons_MT_HFO",
        "Main_Engine_Cons_MT_LFO",
        "Main_Engine_Cons_MT_MGO",
      ],
    },

    {
      id: "speed-aggregation",
      title: "Speed Calculation",
      description: "Daily grouping and averaging using $group stage",
      formula: "$group by date → $avg(speedOverGround), $sum(1)",
      borderColor: "border-blue-200",
      bgColor: "bg-blue-50",
      textColor: "text-blue-700",
      code: `$group: {
      avgSpeed: { $avg: "$speedOverGround" }}`,
      fields: ["speedOverGround"],
    },
  ];

  return (
    <div>
      <div className="mb-6 border border-gray-300 rounded-lg overflow-hidden">
        <button
          className="flex items-center justify-between w-full p-5 bg-gray-50 hover:bg-gray-100 transition-colors"
          onClick={toggleSection}
        >
          <div className="flex items-center gap-4">
            <div className="p-3 bg-[#e9cbf5] rounded-lg">
              <Gauge className="w-6 h-6 text-[#7d3c98]" />
            </div>
            <div className="text-left">
              <h2 className="text-md font-bold text-gray-800">
                Speed & Fuel Consumption Calculations
              </h2>
              <p className="text-gray-600 text-sm">
                Daily aggregation using MongoDB aggregation pipeline from
                VesselMainEngineData
              </p>
            </div>
          </div>
          {openSections ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
        </button>

        {openSections && (
          <div className="p-5 bg-white border-t border-gray-200">
            {/* Calculation Methods */}
            <div className="mb-6">
              <h4 className="font-semibold text-gray-800 mb-3">
                Core Calculations & Logic
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {calculations.map((calc) => (
                  <div
                    key={calc.id}
                    className={`p-5 rounded-lg border ${calc.borderColor} ${calc.bgColor}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <h4 className="font-semibold text-gray-800">
                        {calc.title}
                      </h4>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded ${
                          calc.textColor
                        } ${calc.bgColor.replace("50", "100")}`}
                      >
                        {calc.fields.length} field
                        {calc.fields.length !== 1 ? "s" : ""}
                      </span>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Description:
                      </p>
                      <p className="text-sm text-gray-600">
                        {calc.description}
                      </p>
                    </div>

                    <div className="mb-3">
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Implementation:
                      </p>
                      <div className="text-xs font-mono bg-white p-2 rounded border overflow-x-auto">
                        {calc.code}
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        Source Fields:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {calc.fields.map((field) => (
                          <code
                            key={field}
                            className="text-xs bg-gray-100 p-1 rounded"
                          >
                            {field}
                          </code>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SpeedFuelCalculations;
